import {AlertCircle, ArrowRight} from 'lucide-react';
import {Link} from 'react-router-dom';
import {Button} from '@common-web/components/ui/button';
import {trpc} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {useViewAsStore} from '@common-web/stores/viewAsStore';

/**
 * Prominent on-page card shown on the pipeline / RFC submissions pages when the
 * broker or client/internal admin has RFC submissions awaiting their action.
 * The CTA deep-links to `reviewHref` (the same page with
 * `?filter=action-required`), which those pages read to pre-apply the "Action
 * Required" filter.
 *
 * Renders nothing when there is no outstanding work, for group users, or while
 * a client admin is previewing the portal as a broker (view-as mode).
 */
export function ActionRequiredBanner({reviewHref}: {reviewHref: string}) {
  const user = useAuthStore((s) => s.user);
  const isBrokerUser = useAuthStore((s) => s.isBrokerUser());
  const isGroupUser = useAuthStore((s) => s.isGroupUser());
  const viewAsBroker = useViewAsStore((s) => s.viewAsBroker);

  const {data} = trpc.clients.rfc.pendingActionCount.useQuery(undefined, {
    enabled: !!user && !isGroupUser && !viewAsBroker,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  const count = data?.count ?? 0;
  if (isGroupUser || viewAsBroker || count === 0) return null;

  const noun = isBrokerUser ? 'deal' : 'submission';
  const label = count === 1 ? noun : `${noun}s`;

  return (
    <div className='mb-6 flex flex-col gap-4 rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between'>
      <div className='flex items-center gap-4'>
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100'>
          <AlertCircle className='h-6 w-6 text-blue-600' />
        </div>
        <div>
          <h3 className='text-base font-semibold text-blue-900'>Action required</h3>
          <p className='text-sm text-blue-800'>
            You have <strong>{count}</strong> {label} awaiting your review.
          </p>
        </div>
      </div>
      <Button asChild size='lg' className='shrink-0 bg-blue-600 text-white hover:bg-blue-700'>
        <Link to={reviewHref}>
          Click here to review
          <ArrowRight className='ml-2 h-4 w-4' />
        </Link>
      </Button>
    </div>
  );
}
